import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import EvidenceUpload from './EvidenceUpload';

const DisputeDetailModal = ({ dispute, statusConfig, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [responseText, setResponseText] = useState('');
  const [responseEvidence, setResponseEvidence] = useState([]);
  const [showResponseForm, setShowResponseForm] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'evidence', label: 'Evidence', icon: 'Image' },
    { id: 'timeline', label: 'Timeline', icon: 'Clock' },
    { id: 'responses', label: 'Responses', icon: 'MessageSquare', badge: dispute?.responses?.length }
  ];

  const handleSubmitResponse = () => {
    if (!responseText?.trim()) return;

    const newResponse = {
      id: dispute?.responses?.length + 1,
      sender: 'You',
      role: dispute?.opposingPartyRole === 'owner' ? 'renter' : 'owner',
      message: responseText,
      timestamp: new Date()?.toISOString(),
      evidence: responseEvidence
    };

    const updatedDispute = {
      ...dispute,
      responses: [...dispute?.responses, newResponse],
      status: 'under_review',
      lastUpdate: new Date()?.toISOString()?.split('T')?.[0],
      timeline: [
        ...dispute?.timeline,
        {
          date: new Date()?.toISOString(),
          action: 'Response submitted',
          actor: 'You',
          description: 'Response and counter-evidence submitted'
        }
      ]
    };

    onUpdate(updatedDispute);
    setResponseText('');
    setResponseEvidence([]);
    setShowResponseForm(false);
    setActiveTab('responses');
  };

  const handleRequestMediation = () => {
    const updatedDispute = {
      ...dispute,
      status: 'mediation',
      lastUpdate: new Date()?.toISOString()?.split('T')?.[0],
      timeline: [
        ...dispute?.timeline,
        {
          date: new Date()?.toISOString(),
          action: 'Mediation requested',
          actor: 'You',
          description: 'Case escalated to platform mediation'
        }
      ]
    };
    onUpdate(updatedDispute);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="font-heading text-2xl font-bold text-foreground">
                  {dispute?.title}
                </h2>
                <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${statusConfig?.color}`}>
                  <Icon name={statusConfig?.icon} size={14} strokeWidth={2} />
                  <span className="font-caption text-xs font-semibold">{statusConfig?.label}</span>
                </div>
              </div>
              <p className="font-caption text-sm text-muted-foreground">
                Case #{dispute?.caseNumber} • Filed {dispute?.createdDate}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth"
            >
              <Icon name="X" size={24} strokeWidth={2} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mt-4 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md font-body font-medium transition-smooth whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={18} strokeWidth={2} />
                <span>{tab?.label}</span>
                {tab?.badge > 0 && (
                  <span className="bg-primary-foreground text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                    {tab?.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Trailer Info */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-body font-semibold text-foreground mb-3">Trailer Information</h3>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-16 h-16 rounded overflow-hidden">
                      <Image
                        src={dispute?.trailerImage}
                        alt={dispute?.trailerImageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-body font-semibold text-foreground">{dispute?.trailerName}</p>
                      <p className="font-caption text-xs text-muted-foreground">Rental Trailer</p>
                    </div>
                  </div>
                </div>

                {/* Parties */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-body font-semibold text-foreground mb-3">Involved Parties</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-caption text-sm text-muted-foreground">You</span>
                      <span className="font-body text-sm font-semibold text-foreground">Claimant</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-caption text-sm text-muted-foreground">{dispute?.opposingParty}</span>
                      <span className="font-body text-sm font-semibold text-foreground">
                        {dispute?.opposingPartyRole?.charAt(0)?.toUpperCase() + dispute?.opposingPartyRole?.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Claim Details */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="font-body font-semibold text-foreground mb-3">Claim Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="font-caption text-xs text-muted-foreground mb-1">Dispute Type</p>
                    <p className="font-body text-sm font-semibold text-foreground">
                      {dispute?.disputeType?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </p>
                  </div>
                  <div>
                    <p className="font-caption text-xs text-muted-foreground mb-1">Claim Amount</p>
                    <p className="font-body text-sm font-semibold text-foreground">${dispute?.amount}</p>
                  </div>
                  <div>
                    <p className="font-caption text-xs text-muted-foreground mb-1">Priority</p>
                    <p className="font-body text-sm font-semibold text-foreground">
                      {dispute?.priority?.charAt(0)?.toUpperCase() + dispute?.priority?.slice(1)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-2">Description</p>
                  <p className="font-body text-sm text-foreground">{dispute?.description}</p>
                </div>
              </div>

              {/* Resolution (if resolved) */}
              {dispute?.resolution && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={24} className="text-success flex-shrink-0" strokeWidth={2} />
                    <div className="flex-1">
                      <h3 className="font-body font-semibold text-foreground mb-2">Resolution</h3>
                      <p className="font-body text-sm text-foreground mb-2">{dispute?.resolution?.outcome}</p>
                      <p className="font-caption text-xs text-muted-foreground mb-2">
                        Resolved by {dispute?.resolution?.resolvedBy} on {dispute?.resolution?.resolvedDate}
                      </p>
                      {dispute?.resolution?.amount && (
                        <p className="font-body text-sm font-semibold text-success">
                          Amount: ${dispute?.resolution?.amount}
                        </p>
                      )}
                      {dispute?.resolution?.notes && (
                        <p className="font-caption text-xs text-muted-foreground mt-2">
                          {dispute?.resolution?.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Evidence Tab */}
          {activeTab === 'evidence' && (
            <div className="space-y-4">
              <h3 className="font-body font-semibold text-foreground">Submitted Evidence</h3>
              {dispute?.evidence?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Image" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-body text-muted-foreground">No evidence submitted</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dispute?.evidence?.map((item) => (
                    <div key={item?.id} className="bg-muted/30 rounded-lg overflow-hidden border border-border">
                      {item?.type === 'image' ? (
                        <img
                          src={item?.url}
                          alt={item?.alt}
                          className="w-full h-48 object-cover"
                        />
                      ) : (
                        <div className="w-full h-48 bg-primary/10 flex items-center justify-center">
                          <Icon
                            name={item?.type === 'video' ? 'Video' : 'FileText'}
                            size={48}
                            className="text-primary"
                            strokeWidth={1.5}
                          />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="font-body text-sm font-semibold text-foreground truncate">
                          {item?.name}
                        </p>
                        <p className="font-caption text-xs text-muted-foreground">
                          {item?.type?.charAt(0)?.toUpperCase() + item?.type?.slice(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <h3 className="font-body font-semibold text-foreground">Claim Timeline</h3>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
                <div className="space-y-6">
                  {dispute?.timeline?.map((event, index) => (
                    <div key={index} className="relative flex items-start space-x-4">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 relative z-10">
                        <Icon name="Check" size={16} color="white" strokeWidth={2} />
                      </div>
                      <div className="flex-1 bg-muted/30 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-body font-semibold text-foreground">{event?.action}</p>
                          <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">
                            {formatTimestamp(event?.date)}
                          </span>
                        </div>
                        <p className="font-caption text-sm text-muted-foreground">
                          {event?.description}
                        </p>
                        <p className="font-caption text-xs text-muted-foreground mt-1">
                          By: {event?.actor}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Responses Tab */}
          {activeTab === 'responses' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-body font-semibold text-foreground">Responses & Communications</h3>
                {!showResponseForm && dispute?.status !== 'resolved' && (
                  <Button
                    size="sm"
                    iconName="MessageSquare"
                    onClick={() => setShowResponseForm(true)}
                  >
                    Add Response
                  </Button>
                )}
              </div>

              {/* Response Form */}
              {showResponseForm && (
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="font-body font-semibold text-foreground mb-3">Submit Response</h4>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e?.target?.value)}
                    placeholder="Enter your response..."
                    rows={4}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm mb-3"
                  />
                  <EvidenceUpload
                    evidence={responseEvidence}
                    onChange={setResponseEvidence}
                  />
                  <div className="flex justify-end space-x-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowResponseForm(false);
                        setResponseText('');
                        setResponseEvidence([]);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      iconName="Send"
                      onClick={handleSubmitResponse}
                      disabled={!responseText?.trim()}
                    >
                      Submit Response
                    </Button>
                  </div>
                </div>
              )}

              {/* Responses List */}
              {dispute?.responses?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-body text-muted-foreground">No responses yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dispute?.responses?.map((response) => (
                    <div key={response?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <Icon name="User" size={16} color="white" strokeWidth={2} />
                          </div>
                          <div>
                            <p className="font-body font-semibold text-foreground">{response?.sender}</p>
                            <p className="font-caption text-xs text-muted-foreground">
                              {response?.role?.charAt(0)?.toUpperCase() + response?.role?.slice(1)}
                            </p>
                          </div>
                        </div>
                        <span className="font-caption text-xs text-muted-foreground">
                          {formatTimestamp(response?.timestamp)}
                        </span>
                      </div>
                      <p className="font-body text-sm text-foreground mb-3">{response?.message}</p>
                      {response?.evidence?.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Paperclip" size={14} className="text-muted-foreground" strokeWidth={2} />
                          <span className="font-caption text-xs text-muted-foreground">
                            {response?.evidence?.length} attachment(s)
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {dispute?.status !== 'resolved' && (
          <div className="p-6 border-t border-border">
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <div className="flex gap-2">
                {dispute?.status !== 'mediation' && (
                  <Button
                    variant="warning"
                    iconName="AlertTriangle"
                    onClick={handleRequestMediation}
                  >
                    Request Mediation
                  </Button>
                )}
                {dispute?.status === 'awaiting_response' && (
                  <Button
                    iconName="MessageSquare"
                    onClick={() => {
                      setActiveTab('responses');
                      setShowResponseForm(true);
                    }}
                  >
                    Respond to Claim
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeDetailModal;